import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return undefined; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") { await refresh(); setState("paid"); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("error"); return; }
      } catch (e) {
        console.warn("Payment status check failed, retrying:", e?.message || e);
      }
      if (attempts.current >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-5" data-testid="payment-success-page">
      {state === "checking" && <><Loader2 className="w-12 h-12 mx-auto text-indigo-600 animate-spin" /><h1 className="font-display text-3xl font-bold text-slate-900">Confirming your payment…</h1><p className="text-slate-600">This usually takes a few seconds.</p></>}
      {state === "paid" && <><CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" /><h1 className="font-display text-3xl font-bold text-slate-900" data-testid="payment-paid">You're all set</h1><p className="text-slate-600">Your subscription is active. Invite your first client or explore the library.</p>
        <div className="flex justify-center gap-3"><Button asChild className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"><Link to="/clients" data-testid="success-clients-link">Invite clients</Link></Button><Button asChild variant="outline" className="rounded-full"><Link to="/" data-testid="success-library-link">Open the library</Link></Button></div></>}
      {state === "timeout" && <><Loader2 className="w-12 h-12 mx-auto text-amber-500" /><h1 className="font-display text-3xl font-bold text-slate-900">Still processing</h1><p className="text-slate-600">Your payment is taking longer than usual. Check Billing in a minute — no need to pay again.</p><Button asChild variant="outline" className="rounded-full"><Link to="/billing">Go to Billing</Link></Button></>}
      {state === "error" && <><XCircle className="w-14 h-14 mx-auto text-rose-600" /><h1 className="font-display text-3xl font-bold text-slate-900">Payment not completed</h1><p className="text-slate-600">Nothing was charged. You can try again from Billing.</p><Button asChild className="rounded-full"><Link to="/billing">Back to Billing</Link></Button></>}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-5" data-testid="payment-cancel-page">
      <XCircle className="w-14 h-14 mx-auto text-slate-400" />
      <h1 className="font-display text-3xl font-bold text-slate-900">Checkout cancelled</h1>
      <p className="text-slate-600">No payment was taken. Choose a plan whenever you're ready.</p>
      <Button asChild className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"><Link to="/billing" data-testid="cancel-billing-link">Back to plans</Link></Button>
    </div>
  );
}

export function Locked() {
  const { user, logout } = useAuth();
  const reason = user?.access?.reason;
  const copy = {
    therapist_inactive: ["Your therapist's subscription is paused", "Your account is linked to your therapist's plan. Once it is active again, everything here will unlock automatically. If you think this is a mistake, please check in with them."],
    owner_inactive: ["Your practice's subscription is paused", "Your seat is covered by your practice owner's plan. Ask them to reactivate billing to restore access."],
    no_access: ["This account isn't linked to a clinician", "Clients join by invitation from their therapist. If you are a licensed clinician, please apply for access."],
  }[reason] || ["Access is paused", "An active subscription is required."];
  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-5" data-testid="locked-page">
      <h1 className="font-display text-3xl font-bold text-slate-900">{copy[0]}</h1>
      <p className="text-slate-600">{copy[1]}</p>
      <div className="flex justify-center gap-3">
        {reason === "no_access" && <Button asChild className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"><Link to="/apply">Apply as a clinician</Link></Button>}
        <Button variant="outline" className="rounded-full" onClick={logout} data-testid="locked-logout-button">Log out</Button>
      </div>
    </div>
  );
}
